import { Badge, Button, Chip } from '@uplink/ui';
import { type ReactNode, useEffect, useState } from 'react';
export { providerName } from '../providers.ts';
import { haptic } from '../telegram.ts';

/**
 * Pieces shared by the admin screens. The panel is for the Uplink team, so it speaks Russian only and
 * shows money in dollars, as the ledger keeps it.
 */

export const PERIOD_LABELS = {
  today: 'Сегодня',
  '7d': '7 дней',
  '30d': '30 дней',
  month: 'Этот месяц',
  prevMonth: 'Прошлый месяц',
  all: 'Всё время',
} as const;
export type PeriodName = keyof typeof PERIOD_LABELS;

type Tone = 'neutral' | 'ok' | 'warn' | 'crit' | 'accent';

export const ORDER_STATUS: Record<string, [string, Tone]> = {
  paid: ['оплачен', 'accent'],
  provisioning: ['выполняется', 'accent'],
  active: ['выполнен', 'ok'],
  failed: ['ошибка', 'crit'],
  manual_review: ['на проверке', 'warn'],
  refunded: ['возврат', 'neutral'],
};
export const ORDER_KIND: Record<string, string> = {
  domain_register: 'Регистрация домена',
  domain_renew: 'Продление домена',
  server_create: 'Новый сервер',
};
export const TOPUP_STATUS: Record<string, [string, Tone]> = {
  paid: ['оплачено', 'ok'],
  pending: ['ждёт оплаты', 'accent'],
  expired: ['истекло', 'neutral'],
  failed: ['ошибка', 'crit'],
};
export const SERVER_STATUS: Record<string, [string, Tone]> = {
  pending: ['создаётся', 'accent'],
  provisioning: ['создаётся', 'accent'],
  active: ['работает', 'ok'],
  suspended: ['приостановлен', 'warn'],
  deleted: ['удалён', 'neutral'],
  failed: ['ошибка', 'crit'],
};
export const DOMAIN_STATUS: Record<string, [string, Tone]> = {
  pending: ['регистрируется', 'accent'],
  active: ['активен', 'ok'],
  expired: ['истёк', 'warn'],
  failed: ['ошибка', 'crit'],
};

export const BROADCAST_STATUS: Record<string, [string, Tone]> = {
  draft: ['черновик', 'neutral'],
  sending: ['идёт', 'accent'],
  done: ['завершена', 'ok'],
  cancelled: ['остановлена', 'warn'],
};

/** The amount with the status under it, for the end of a row. */
export function AmountStatus({
  amount,
  map,
  status,
}: {
  amount: ReactNode;
  map: Record<string, [string, Tone]>;
  status: string;
}) {
  return (
    <span className="ul-row__end">
      {amount}
      <StatusBadge map={map} status={status} />
    </span>
  );
}

export function StatusBadge({ map, status }: { map: Record<string, [string, Tone]>; status: string }) {
  const [label, tone] = map[status] ?? [status, 'neutral'];
  return <Badge tone={tone}>{label}</Badge>;
}

/** A value that settles a moment after the last change: search fields don't query on every key. */
export function useDebounced<T>(value: T, ms = 300): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return settled;
}

/** One-line horizontally scrolling choice of filters. */
export function FilterChips<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="ul-chips ul-chips--scroll">
      {options.map((o) => (
        <Chip
          key={o.value}
          selected={o.value === value}
          onClick={() => {
            haptic.select();
            onChange(o.value);
          }}
        >
          {o.label}
        </Chip>
      ))}
    </div>
  );
}

export const PAGE = 30;

/** "31–60 of 214" with previous / next. */
export function Pager({
  offset,
  total,
  onChange,
}: {
  offset: number;
  total: number;
  onChange: (o: number) => void;
}) {
  if (total <= PAGE) return null;
  return (
    <div className="ul-pager">
      <Button
        variant="secondary"
        disabled={offset === 0}
        onClick={() => onChange(Math.max(0, offset - PAGE))}
      >
        Назад
      </Button>
      <span className="ul-pager__info">
        {offset + 1}–{Math.min(offset + PAGE, total)} из {total}
      </span>
      <Button variant="secondary" disabled={offset + PAGE >= total} onClick={() => onChange(offset + PAGE)}>
        Дальше
      </Button>
    </div>
  );
}

export function Kpi({
  label,
  value,
  sub,
  tone,
  onClick,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: 'ok' | 'crit';
  onClick?: () => void;
}) {
  const body = (
    <>
      <span className="ul-kpi__label">{label}</span>
      <span className={`ul-kpi__value${tone ? ` ul-kpi__value--${tone}` : ''}`}>{value}</span>
      {sub ? <span className="ul-kpi__sub">{sub}</span> : null}
    </>
  );
  return onClick ? (
    <button type="button" className="ul-kpi ul-press" onClick={onClick}>
      {body}
    </button>
  ) : (
    <div className="ul-kpi">{body}</div>
  );
}

export interface ChartPoint {
  date: string;
  revenue: number;
  net: number;
}

const DAY_FMT = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short', timeZone: 'UTC' });
const MONTH_FMT = new Intl.DateTimeFormat('ru-RU', { month: 'short', year: 'numeric', timeZone: 'UTC' });

export function bucketLabel(date: string, bucket: 'day' | 'month') {
  const d = new Date(`${bucket === 'day' ? date : `${date}-01`}T00:00:00Z`);
  return (bucket === 'day' ? DAY_FMT : MONTH_FMT).format(d).replace(' г.', '').replace('.', '');
}

/**
 * Revenue bars with the net margin inside them; a negative margin hangs below the axis in red.
 * Tapping a bar shows its numbers above the chart.
 */
export function MarginChart({
  points,
  bucket,
  usd,
}: {
  points: ChartPoint[];
  bucket: 'day' | 'month';
  usd: (cents: number) => string;
}) {
  const [picked, setPicked] = useState<number | null>(null);
  const W = 320;
  const H = 120;
  const top = Math.max(1, ...points.map((p) => p.revenue), ...points.map((p) => p.net));
  const bottom = Math.min(0, ...points.map((p) => p.net));
  const scale = (H - 4) / (top - bottom);
  const zero = 2 + top * scale;
  const slot = W / Math.max(points.length, 1);
  const bar = Math.max(2, Math.min(18, slot * 0.62));
  const shown = picked !== null ? points[picked] : null;
  const total = points.reduce((a, p) => ({ revenue: a.revenue + p.revenue, net: a.net + p.net }), {
    revenue: 0,
    net: 0,
  });

  return (
    <div className="ul-chart">
      <div className="ul-chart__head" aria-live="polite">
        {shown ? (
          <>
            <span className="ul-chart__when">{bucketLabel(shown.date, bucket)}</span>
            <span>
              Выручка <b>{usd(shown.revenue)}</b> · маржа{' '}
              <b className={shown.net < 0 ? 'ul-chart__neg' : undefined}>{usd(shown.net)}</b>
            </span>
          </>
        ) : (
          <>
            <span className="ul-chart__legend ul-chart__legend--rev">Выручка</span>
            <span className="ul-chart__legend ul-chart__legend--net">Чистая маржа</span>
          </>
        )}
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        className="ul-chart__svg"
        role="img"
        aria-label={`Выручка ${usd(total.revenue)}, чистая маржа ${usd(total.net)}`}
      >
        <line x1="0" x2={W} y1={zero} y2={zero} className="ul-chart__axis" />
        {points.map((p, i) => {
          const x = i * slot + (slot - bar) / 2;
          const rev = p.revenue * scale;
          const net = Math.abs(p.net) * scale;
          return (
            // biome-ignore lint/a11y/noStaticElementInteractions: the totals are in the label; tapping a bar is a convenience
            <g key={p.date} onClick={() => setPicked(picked === i ? null : i)} className="ul-chart__col">
              <rect x={i * slot} y={0} width={slot} height={H} fill="transparent" />
              <rect
                x={x}
                y={zero - rev}
                width={bar}
                height={rev}
                rx={Math.min(3, bar / 2)}
                className="ul-chart__rev"
                opacity={picked === null || picked === i ? 1 : 0.45}
              />
              <rect
                x={x + bar * 0.2}
                y={p.net >= 0 ? zero - net : zero}
                width={bar * 0.6}
                height={net}
                rx={Math.min(2, bar / 4)}
                className={p.net >= 0 ? 'ul-chart__net' : 'ul-chart__net ul-chart__net--neg'}
                opacity={picked === null || picked === i ? 1 : 0.45}
              />
            </g>
          );
        })}
      </svg>
      <div className="ul-chart__axis-labels">
        <span>{points[0] ? bucketLabel(points[0].date, bucket) : ''}</span>
        <span>{points.at(-1) ? bucketLabel(points.at(-1)!.date, bucket) : ''}</span>
      </div>
    </div>
  );
}
